import { nextTick, readonly, ref, shallowRef, watch, type Ref } from "vue";
import { useFocusTrap } from "@vueuse/integrations/useFocusTrap";
import { onClickOutside } from "@vueuse/core";

export type CloseReason = "escape" | "outside";
export type InitialFocus =
  | string
  | ((root: HTMLElement) => HTMLElement | null | undefined);

/**
 * Focus-restoration options for pairing `usePopupTrap` with `usePopupMenu`.
 *
 * `usePopupMenu` owns menu-close focus restoration. These settings disable
 * both competing restoration paths in `usePopupTrap` and the underlying
 * `focus-trap` implementation. Spread this preset last so those integration
 * settings take precedence.
 */
export const POPUP_MENU_TRAP_OPTIONS = {
  returnFocusToTrigger: false,
  focusTrapOptions: {
    returnFocusOnDeactivate: false,
  },
} as const;

export function usePopupTrap(opts: {
  isOpen: Ref<boolean>;
  onRequestClose?: (reason: CloseReason, ev?: Event) => void;
  popupSelector?: string | string[];
  triggerEl?: Ref<HTMLElement | null>;
  resolvePopupEl?: () => HTMLElement | null;
  initialFocus?: InitialFocus;
  focusTrapOptions?: Parameters<typeof useFocusTrap>[1];
  returnFocusToTrigger?: boolean;
}) {
  const popupRef = shallowRef<HTMLElement | null>(null);

  const defaultSelectors = [
    ".k-animation-container .k-popup",
    ".k-popup",
    ".k-timepicker-popup",
    ".k-menu-popup",
  ];

  const selectors = Array.isArray(opts.popupSelector)
    ? opts.popupSelector
    : [opts.popupSelector ?? defaultSelectors].flat();

  const pickInitialFocus = (root: HTMLElement) => {
    if (!root) return null as unknown as HTMLElement;
    if (typeof opts.initialFocus === "string") {
      const found = root.querySelector(opts.initialFocus) as HTMLElement | null;
      return found ?? root;
    }
    if (typeof opts.initialFocus === "function") {
      return (opts.initialFocus(root) as HTMLElement | null) ?? root;
    }
    const candidate = root.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    ) as HTMLElement | null;
    return candidate ?? root;
  };

  const { activate, deactivate } = useFocusTrap(popupRef, {
    escapeDeactivates: false,
    clickOutsideDeactivates: false,
    // Without this, focus-trap's own default (false) preventDefault()s and
    // swallows every outside pointerdown/click while active - not just ones
    // that would close it - blocking interaction with anything else on the
    // page (e.g. another trigger in the same row) until the trap deactivates.
    allowOutsideClick: true,
    fallbackFocus: () => popupRef.value as HTMLElement,
    initialFocus: () => pickInitialFocus(popupRef.value as HTMLElement),
    ...(opts.focusTrapOptions ?? {}),
  });

  let escapeHandler: ((e: KeyboardEvent) => void) | null = null;
  let lastCloseReason = ref<CloseReason | null>(null);

  const findPopup = (): HTMLElement | null => {
    if (opts.resolvePopupEl) return opts.resolvePopupEl();

    const pickLastVisible = (root: ParentNode, sel: string) => {
      const list = Array.from(root.querySelectorAll(sel)) as HTMLElement[];
      if (!list.length) return null as HTMLElement | null;
      const visible = list.filter(
        (el) => el.offsetParent !== null || el.getClientRects().length > 0,
      );
      return visible[visible.length - 1] ?? list[list.length - 1] ?? null;
    };

    const scopeRoot =
      opts.triggerEl?.value?.closest?.(".k-animation-container") ??
      document.body;

    for (const sel of selectors) {
      const el = pickLastVisible(scopeRoot, sel);
      if (el) return el;
    }

    for (const sel of selectors) {
      const el = pickLastVisible(document, sel);
      if (el) return el;
    }

    return null;
  };

  onClickOutside(popupRef, (e) => {
    lastCloseReason.value = "outside";
    if (opts.isOpen.value) {
      opts.onRequestClose?.("outside", e);
    }
  });

  watch(
    () => opts.isOpen.value,
    (open) => {
      if (open) {
        // Reset on open so the previous reason remains visible while closed
        lastCloseReason.value = null;
        nextTick(() => {
          const popup = findPopup();
          if (!popup) return;
          // Guarantees a valid fallback/initial focus target even when no
          // descendant is currently tabbable (e.g. Kendo Menu items only gain
          // a live tabindex via its own roving-tabindex JS on interaction) -
          // without this, focus-trap's activate() throws and silently no-ops,
          // leaving Tab to escape the popup entirely.
          if (popup.tabIndex < 0) popup.tabIndex = -1;
          popupRef.value = popup;
          setTimeout(() => {
            try {
              activate();
            } catch (error) {
              console.warn(
                "usePopupTrap: focus trap failed to activate",
                error,
              );
            }
          }, 0);

          if (!escapeHandler) {
            escapeHandler = (e: KeyboardEvent) => {
              if (e.key === "Escape") {
                lastCloseReason.value = "escape";
                if (opts.isOpen.value) {
                  opts.onRequestClose?.("escape", e);
                }
              }
            };
            // Capture on document to catch Escape even when focus
            // stays on the trigger (e.g., TimeInput masked field).
            document.addEventListener("keydown", escapeHandler, true);
          }
        });
      } else {
        deactivate();
        if (escapeHandler) {
          document.removeEventListener("keydown", escapeHandler, true);
          escapeHandler = null;
        }
        popupRef.value = null;

        // On outside close, ensure trigger/input is not focused
        if (lastCloseReason.value === "outside") {
          nextTick(() => {
            const doBlur = () => {
              const root = opts.triggerEl?.value as HTMLElement | null;
              const input = root?.querySelector("input") as HTMLElement | null;
              const active = document.activeElement as HTMLElement | null;
              // Only blur if focus is still on the trigger/input to avoid interfering
              if (
                active &&
                (active === input || (root && root.contains(active)))
              ) {
                (input ?? root)?.blur?.();
              }
            };
            requestAnimationFrame(() => setTimeout(doBlur, 0));
          });
        }

        if (
          opts.returnFocusToTrigger !== false &&
          lastCloseReason.value === "escape"
        ) {
          if (opts.triggerEl?.value) {
            // Schedule after Vue updates and after event/click propagation
            nextTick(() => {
              const doFocus = () => {
                const root = opts.triggerEl?.value as HTMLElement | null;
                if (!root) return;
                const input = root.querySelector("input") as HTMLElement | null;
                const target = (input ?? root) as HTMLElement | null;
                target?.focus?.();
              };
              requestAnimationFrame(() => setTimeout(doFocus, 0));
            });
          }
        }
      }
    },
  );

  return {
    popupRef,
    activate,
    deactivate,
    lastCloseReason: readonly(lastCloseReason),
    setPopupEl: (el: HTMLElement | null) => (popupRef.value = el),
  };
}
